import React from 'react';
import FunnelStagesList from '../../settings/funnel/components/FunnelStagesList';
import { stageNumberTone } from '../../settings/funnel/components/FunnelStageRow';
import { Button } from '../../../components/ui/Button';
import { useFunnelOnboardingStep } from '../hooks/useFunnelOnboardingStep';
import OnboardingStepFooter from './OnboardingStepFooter';

type Props = {
    onBackToPrevious: () => void;
};

const ACTION_BTN = 'min-h-11 w-full touch-manipulation sm:w-auto';
const STATUS_BOX =
    'rounded-xl border border-dashed border-border bg-surface-subtle px-s4 py-s6 text-center text-sm text-muted';

const FunnelOnboardingStep: React.FC<Props> = ({ onBackToPrevious }) => {
    const {
        funnelId,
        stages,
        form,
        isEditing,
        setIsEditing,
        busy,
        skipping,
        loadingStages,
        onConfirm,
        onSave,
        onSkip,
    } = useFunnelOnboardingStep();

    let body: React.ReactNode;
    if (!funnelId) {
        body = (
            <p role="status" className={STATUS_BOX}>
                Nenhum funil encontrado. Crie um funil nas configurações.
            </p>
        );
    } else if (isEditing) {
        body = (
            <FunnelStagesList
                stages={form.stageList}
                draggedIndex={form.draggedIndex}
                showConversionEvent={false}
                onNameChange={form.updateStageName}
                onDeadlineChange={form.updateStageDeadline}
                onRemove={form.removeStage}
                onDragStart={form.handleDragStart}
                onDragOver={form.handleDragOver}
                onDragEnd={form.handleDragEnd}
                onAddStage={form.addStage}
                onAddStageAt={form.addStageAt}
            />
        );
    } else if (loadingStages && stages.length === 0) {
        body = (
            <p role="status" className="py-s6 text-center text-sm text-muted">
                Carregando etapas...
            </p>
        );
    } else if (stages.length === 0) {
        body = (
            <p role="status" className={STATUS_BOX}>
                Nenhuma etapa neste funil. Clique em Editar para adicionar.
            </p>
        );
    } else {
        body = (
            <ol className="divide-y divide-border">
                {stages.map((stage, i) => (
                    <li
                        key={stage.id}
                        className="flex items-center gap-s3 py-s3 first:pt-0 last:pb-0"
                    >
                        <span
                            className="flex size-7 shrink-0 items-center justify-center rounded-full font-mono text-xs font-semibold tabular-nums"
                            style={stageNumberTone(i, stage.color)}
                            aria-hidden
                        >
                            {i + 1}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-default">
                            {stage.name}
                        </span>
                    </li>
                ))}
            </ol>
        );
    }

    const primaryLabel = isEditing
        ? busy
            ? 'Salvando...'
            : 'Salvar'
        : busy
          ? 'Avançando...'
          : 'Avançar';

    return (
        <div className="flex flex-1 flex-col">
            {body}

            <footer className="mt-s4 border-t border-border pt-s4 sm:mt-s6 sm:pt-s5">
                <div className="flex flex-col-reverse gap-s2 sm:flex-row sm:gap-s3">
                    <Button
                        type="button"
                        className={ACTION_BTN}
                        onClick={isEditing ? onSave : onConfirm}
                        disabled={isEditing ? busy : busy || !funnelId}
                    >
                        {primaryLabel}
                    </Button>
                    <Button
                        type="button"
                        className={ACTION_BTN}
                        variant="outline"
                        onClick={() => setIsEditing(!isEditing)}
                        disabled={isEditing ? busy : !funnelId}
                    >
                        {isEditing ? 'Cancelar' : 'Editar'}
                    </Button>
                </div>

                <OnboardingStepFooter
                    className="mt-s3 border-0 pt-0 sm:mt-s4"
                    onSkip={onSkip}
                    isSkipping={skipping}
                    onBack={isEditing ? undefined : onBackToPrevious}
                />
            </footer>
        </div>
    );
};

export default FunnelOnboardingStep;
